import { Link, Stack, Tooltip } from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { orderSciEventsApi } from 'api';
import { DescriptionRow } from 'components';
import { QueryKey } from 'enums';
import { useUser } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { PayinOrder } from 'types';
import { orderUtils } from 'utils';

import { SciEventsDialog } from './SciEventsDialog';

type Props = {
  order: PayinOrder;
};

export const SciEvents: React.FC<Props> = ({ order }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.sci_events',
  });
  const { isAdminOrTechOperator } = useUser();

  const [dialogOpen, setDialogOpen] = useState(false);

  const queryResult = useQuery(
    [QueryKey.OrderSciEvents, order.id],
    orderSciEventsApi.getForOrder(order.id),
    { enabled: dialogOpen },
  );

  const gatewayLink = useMemo(
    () => orderUtils.getPaymentGatewayLink(order),
    [order],
  );

  return (
    <Fragment>
      <DescriptionRow
        title={t('title')}
        value={
          <Stack direction="row" spacing={2}>
            <Link
              component="button"
              variant="body2"
              underline="none"
              onClick={() => setDialogOpen(true)}
            >
              {t('open_events')}
            </Link>
            {isAdminOrTechOperator && (
              <Tooltip
                title={gatewayLink.disabled ? gatewayLink.disabledReason : ''}
              >
                <span>
                  <Link
                    component={gatewayLink.disabled ? 'button' : 'a'}
                    variant="body2"
                    underline="none"
                    disabled={gatewayLink.disabled}
                    href={
                      gatewayLink.disabled
                        ? undefined
                        : gatewayLink.url ?? undefined
                    }
                    target={gatewayLink.disabled ? undefined : '_blank'}
                    rel={
                      gatewayLink.disabled ? undefined : 'noopener noreferrer'
                    }
                  >
                    {t('open_gateway')}
                  </Link>
                </span>
              </Tooltip>
            )}
          </Stack>
        }
      />
      <SciEventsDialog
        queryResult={queryResult}
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
      />
    </Fragment>
  );
};
